import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme as useAppTheme } from 'context/ThemeContext';
import { useApi } from 'context/ApiProvider';
import { consumeTemplateLoadPendingClose, loadPrivateTemplate } from '../../../services/designService';
import { useDesignStore } from 'context/DesignContext';
import { useLoadingComponentStore } from "context/LoadingContext";
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { InsoleDesign } from "models/InsoleDesign";
import { Engraving } from 'models/Engraving';
import { showAlertDialog } from 'components/Dialog/dialog';

interface LoadTemplatePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export enum TemplateType {
  Private = "Private",
  Public = "Public"
}

interface TemplateItem {
  templateId: number;
  name: string;
  category: string;
  templateType: TemplateType;
  image: string; 
}

interface Category {
  name: string;
  templates: TemplateItem[];
}

const LoadTemplatePanel: React.FC<LoadTemplatePanelProps> = ({ isOpen, onClose }) => {
  const { isDarkMode } = useAppTheme();
  const api = useApi();

  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState<number | null>(null);
  const designLoaded = useDesignStore((state) => state.designLoaded);


  const handleClose = () => {
    setSelectedTemplate(null); 
    onClose();
  };
const handleDeleteTemplate = async (template: TemplateItem) => {
  const result = await showAlertDialog({
        title: "Removing a Template",
        description: "Are you sure to remove this template?",
        agreeText: "Yes",
        disagreeText: "No",
      });
  
  if (!result) return;
  try {
    setDeletingTemplateId(template.templateId);
    await api.delete(`/api/templates/${template.templateId}`);
    setCategories((prev) =>
      prev.map((cat) => ({
        ...cat,
        templates: cat.templates.filter((t) => t.templateId !== template.templateId),
      }))
    );
  } catch (error) {
    console.error('Error deleting template:', error);
  } finally {
    setDeletingTemplateId(null);
  }
};


const loadTemplateToViewer = async (api: any, template: TemplateItem) => {
  const currentDesignId = useSelectedDesignStore.getState().designId;
  const currentDesign = useDesignStore.getState().insoleDesign;
  const currentEngraving = useDesignStore.getState().engraving;
  if (currentDesign?.designType === 'General') {
    useSelectedDesignStore.setState({ previousDesignId: currentDesignId });
    useDesignStore.setState({ previousDesign: InsoleDesign.cloneFrom(currentDesign)});
    useDesignStore.setState({ previousEngraving: Engraving.cloneFrom(currentEngraving)});
  }

  try{
    const newId = await loadPrivateTemplate(api, template.templateId);
    if (newId<=0) {
      console.error("Failed to load design");
      return;
    }

  }catch(error){
    console.error("Error loading design:", error);
  }
  finally{
  }
};

  useEffect(() => {
    if (!isOpen || !designLoaded) return;
    if (consumeTemplateLoadPendingClose()) handleClose();
  }, [designLoaded, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchTemplates = async () => {
      setIsLoading(true);
      try {
        const res = await api.get('/api/templates');
        const data: TemplateItem[] = res.body;

        const grouped: Category[] = [];

        for (const item of data) {
          const catName = item.category && item.category.trim() !== '' ? item.category : 'default';

          let category = grouped.find(c => c.name === catName);
          if (!category) {
            category = { name: catName, templates: [] };
            grouped.push(category);
          }
          category.templates.push(item);
        }

        setCategories(grouped);
      } catch (error) {
        console.error('Error fetching templates:', error);
        setCategories([]);
      } finally {
      setIsLoading(false);
    }
    };

    fetchTemplates();
  }, [isOpen, api]);

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby="load-template-title" closeAfterTransition sx={{display: "flex", alignItems: "center", justifyContent: "center",}}>
      <Box
        sx={{
          bgcolor: isDarkMode ? '#333' : '#fff',
          color: isDarkMode ? '#fff' : '#000',
          borderRadius: 2,
          width: { xs: '95vw', md: '95vw', lg: '40vw' },
          maxHeight: { xs: '95vh', md: '95vh', lg: '80vh' },  
          mx: 'auto',
          boxShadow: 24,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          position: 'relative',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #ccc' }}>
          <Typography variant="h6" sx={{ color: isDarkMode ? '#fff' : '#076596' }}>
            Load Template
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon sx={{ color: isDarkMode ? '#fff' : '#000' }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            p: 3,
            position: 'relative',
          }}
        >
          {!isLoading && (
            categories.length === 0 ||
            categories.every(category => category.templates.length === 0)
          ) ? (
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                color: '#999',
                fontStyle: 'italic',
              }}
            >
              No Templates Found
            </Typography>
          ) : (
            categories
              .filter(category => category.templates.length > 0)
              .map((category) => (
                <Box
                  key={category.name}
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    mb: 1,
                  }}
                >
                  {/* Category Header */}
                  <Box
                    sx={{
                      bgcolor: '#f0f0f0',
                      color: '#000',
                      p: 1,
                      borderBottom: '1px solid #ccc',
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {category.name}
                    </Typography>
                  </Box>

                  {/* Templates */}
                  <Box sx={{ p: 1,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 100px))',
                            justifyContent: 'space-between',
                            justifyItems: 'start',
                            gap: 2,}}>
                    {category.templates.map((template) => (
                      <Box
                        key={template.templateId}
                        onClick={() => setSelectedTemplate(template)}
                        onDoubleClick={() => loadTemplateToViewer(api, template)}
                        sx={{
                          width: 100,
                          cursor: 'pointer',
                          textAlign: 'center',
                          userSelect: 'none',
                          border: selectedTemplate?.templateId === template.templateId ? '2px solid #076596' : '2px solid transparent',
                          borderRadius: 2,
                        }}
                      >
                        {template.image ? (
                          <Box
                            component="img"
                            src={`data:image/png;base64,${template.image}`}
                            alt={template.name}
                            sx={{
                              width: '100%',
                              height: 100,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid #ccc',
                              transition: 'border-color 0.3s',
                              '&:hover': { borderColor: '#076596' },
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 100,
                              backgroundColor: '#ddd',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#999',
                              mb: 1,
                            }}
                          >
                            No Image
                          </Box>
                        )}
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            height: 22,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            ...(template.templateType !== TemplateType.Public && {
                              '&:hover .template-name': { opacity: 0 },
                              '&:hover .delete-icon': { opacity: 1, pointerEvents: 'auto' },
                            }),
                          }}
                        >
                          {/* Template Name */}
                          <Typography
                            className="template-name"
                            sx={{
                              fontSize: '0.9rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              transition: 'opacity 0.15s ease',
                              cursor: 'pointer',
                            }}
                          >
                            {template.name}
                          </Typography>

                          {/* Delete Icon (same place) */}
                          <IconButton
                            className="delete-icon"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTemplate(template);
                            }}
                            sx={{
                              position: 'absolute',
                              opacity: 0,
                              pointerEvents: 'none',
                              transition: 'opacity 0.15s ease',
                              color: '#d32f2f',
                              p: 0.25,
                            }}
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #ccc', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ color: isDarkMode ? '#fff' : '#000', 
                borderColor: '#aaa', 
                width: '80px', 
                fontWeight: 500, }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ color: '#fff', 
                borderColor: '#aaa', 
                backgroundColor: '#076596',
                width: '80px',
                fontWeight: 500,
                px: 4,
                '&:hover': { backgroundColor: '#0a6fb4' } }}
            disabled={!selectedTemplate}
            onClick={() => selectedTemplate && loadTemplateToViewer(api, selectedTemplate)}
            >
            Load
            </Button>
        </Box>

        {/* Loading Indicator */}
        {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                bgcolor: 'rgba(212, 212, 212, 0.47)',
                zIndex: 50,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 2,
              }}
            >
              <CircularProgress sx={{ color: '#076596' }} />
            </Box>
          )}

        {/* Context Menu */}
        {/* <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() =>
            contextMenu?.template && handleDeleteTemplate(contextMenu.template)
          }
          disabled={deletingTemplateId === contextMenu?.template?.templateId}
        >
          {deletingTemplateId === contextMenu?.template?.templateId ? (
            <CircularProgress size={16} />
          ) : (
            '🗑'
          )}
          Delete Template
        </MenuItem>
      </Menu> */}
      </Box>
    </Modal>
  );
};

export default LoadTemplatePanel;
